import { Page } from '../../../layout/Page';
import { MutedText, Text } from '../../../newComponents/Texts';
import { useNextPeakClient } from '../../../services/nextpeakClient/clientProvider.tsx';
import {
  useGetAnswers,
  useGetOrganizationPeriodsV2,
  useGetOrganizationUsersV2,
  useGetPositions,
  useGetRoles,
} from '../../../http/hooks.ts';
import {
  IAnswerForStats,
  IOrganizationUser,
  IPeriod,
  IPosition,
  IPositionRole,
} from '../../../services/nextpeakClient';
import Loader from '../../../common/Loader';
import { Heading3, Heading4 } from '../../../newComponents/Headings';
import Card from '../../../newComponents/Card/Card.tsx';
import { useCallback, useMemo, useState } from 'react';
import { IGetAnswersFilter } from '../../../services/nextpeakClient/endpoints/getAnswers.ts';
import { calculateAverageScore } from '../../Assessments/utils.ts';
import FilterDropdown from './FilterDropdown.tsx';
import ProgressBar from '../../../newComponents/ProgressBar';
import Checkbox from '../../../newComponents/forms/Checkbox.tsx';
import MultiSelect from '../../../newComponents/forms/MultiSelect.tsx';
import { Button } from '../../../newComponents/Button/Button.tsx';
import { getMe } from '../../../MeProvider.ts';
import { formatPeriodName } from '../../../utils.ts';
import AggregateChart from './AggregateChart.tsx'

function groupByLevel<T extends { level: string }>(items: T[]): Record<string, T[]> {
	return items.reduce(
		(groups, item) => {
			const level = item.level
			if (!groups[level]) {
				groups[level] = []
			}
			groups[level].push(item)
			return groups
		},
		{} as Record<string, T[]>
	)
}

interface RoleAnswersGraphViewProps {
  answers: IAnswerForStats[]
  positions: IPosition[]
  users: IOrganizationUser[]
  splitByLevel: boolean
}

const RoleAnswersGraphView = ({ answers, positions, users, splitByLevel }: RoleAnswersGraphViewProps) => {
  const answersAvailable = (answers && answers.length > 0);

  const groupedAnswers = useMemo(() => {
    return groupByLevel(answers);
  }, [answers])

  return (
		<>
			{splitByLevel ? (
				<div className="flex flex-col gap-3">
          {positions.map((position) => {
            const level = position.level;
            const gprAnswers = groupedAnswers[level] || [];
            const filteredUsers = users.filter((user) => user.positionId === position.id);
            return (
							<div key={position.id} className="pt-6 mt-6 border-t border-gary-200 dark:border-gray-800">
								<Heading4 className="mb-4">{level}</Heading4>
								{!!gprAnswers.length && <ProgressBar className="grow" showPercentage progress={calculateAverageScore(gprAnswers)} />}
								{!gprAnswers.length && <MutedText className="block text-center">No data</MutedText>}
								<div className="flex justify-between items-center gap-2 mt-2">
									<Text>Employees in bracket</Text>
									<MutedText className="block">{filteredUsers.length}</MutedText>
								</div>
							</div>
						)
          })}
				</div>
			) : (
				<>
					{!answersAvailable && (
            <MutedText className="block text-center mt-8 mb-3">No data</MutedText>
          )}
					{answersAvailable && <AggregateChart progress={calculateAverageScore(answers)} />}
					<div className="pt-6 mt-6 border-t border-gary-200 dark:border-gray-800">
            <div className="flex justify-between items-center gap-2">
						  <Text>Employees in bracket</Text>
						  <MutedText className="block">{users.length}</MutedText>
            </div>
					</div>
				</>
			)}
		</>
	)
}

interface RoleAnswersGraphControlProps {
  role: string
  positions: IPosition[]
  splitByLevel: boolean
  users: IOrganizationUser[]
  periods: IPeriod[]
}

const RoleAnswersGraphControl = ({ role, positions, splitByLevel, users, periods }: RoleAnswersGraphControlProps) => {
  const [selectedFilters, setSelectedFilters] = useState<IGetAnswersFilter>({
    role,
    level: '',
    responseType: '',
  })

  const activeFilters = useMemo(() => {
    const filters: IGetAnswersFilter = {};

    if (selectedFilters.role && role) {
      filters.role = role;
    }

    if (selectedFilters.level) {
      filters.level = selectedFilters.level;
    }

    if (selectedFilters.responseType) {
      filters.responseType = selectedFilters.responseType;
    }

    if (periods.length > 0) {
      filters.periodId = periods[0].id; // TODO: Allow multiple filter values
    }

    return filters;
  }, [selectedFilters, role, positions])

  const client = useNextPeakClient()
  const { data: answers, isLoading } = useGetAnswers(client, { filters: activeFilters })

  const updateLevelFilter = useCallback((level: string) => {
    setSelectedFilters(prev => ({
      ...prev,
      level,
    }));
  }, [])

  const updateResponseTypeFilter = useCallback((responseType: string) => {
    setSelectedFilters(prev => ({
      ...prev,
      responseType,
    }));
  }, [])

  const clearAllFilters = useCallback(() => {
    setSelectedFilters({
      role,
      level: '',
    });
  }, [])

  // const hasActiveFilters = useMemo(() => {
  //   return Object.keys(activeFilters).length > 1 // More than just the role filter
  // }, [activeFilters])

  return (
    <div className={`flex flex-col ${!splitByLevel ? 'justify-between' : ''} h-full`}>
      <>
        <div className="flex mb-4 items-center justify-between">
          <Heading3>{role}</Heading3>
          <FilterDropdown updateResponseTypeFilter={updateResponseTypeFilter} selectedFilters={selectedFilters} positions={positions} updateLevelFilter={updateLevelFilter} clearAllFilters={clearAllFilters} />
        </div>
        {isLoading && <Loader />}
        {!isLoading && answers && <RoleAnswersGraphView answers={answers} positions={positions} users={users} splitByLevel={splitByLevel} />}
      </>
    </div>
  )
}

interface RoleStatisticsProps {
  role: IPositionRole
  splitByLevel: boolean
  periods: IPeriod[]
}

const RoleStatistics = ({ role: { role }, splitByLevel, periods }: RoleStatisticsProps) => {
  const { activeOrg } = getMe()
  const client = useNextPeakClient()
  const { data: positions } = useGetPositions(client, { role: role })
  const { data: users } = useGetOrganizationUsersV2(client, { organizationId: activeOrg?.id!, filters: { role, includes: ['user'] } }, { enabled: !!positions?.length })

  return (
		<Card>
			{positions && users && <RoleAnswersGraphControl role={role} periods={periods} positions={positions} splitByLevel={splitByLevel} users={users} />}
		</Card>
	)
}

const RolesGrid = ({ roles, periods }: { roles: IPositionRole[]; periods: IPeriod[] }) => {
  const [splitByLevel, setSplitByLevel] = useState<boolean>(false)
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [selectedPeriodIds, setSelectedPeriodIds] = useState<string[]>([])

  const toggleSplitByLevel = useCallback(() => {
    setSplitByLevel(prevState => !prevState);
  }, [])

  const updateRoleFilter = useCallback((roles: string[]) => {
    setSelectedRoles(roles);
  }, [])

  const updatePeriodFilter = useCallback((periods: string[]) => {
    setSelectedPeriodIds(periods);
  }, [])

  const clearFilters = useCallback(() => {
    setSelectedRoles([]);
    setSelectedPeriodIds([]);
  }, [])

  const filteredRoles = useMemo(() => {
    if (selectedRoles.length === 0) {
      return roles;
    }
    return roles.filter(role => selectedRoles.includes(role.role));
  }, [roles, selectedRoles])

  const filteredPeriods = useMemo(() => {
    if (selectedPeriodIds.length === 0) {
      return periods;
    }
    return periods.filter(period => selectedPeriodIds.includes(period.id));
  }, [roles, selectedRoles])

  return (
    <>
      <div className="flex justify-end items-center">
        <Checkbox label="Split by levels" onChange={toggleSplitByLevel} checked={splitByLevel} id="splitByLevel" />
        <MultiSelect label="Roles" selectedOptions={selectedRoles} onChange={updateRoleFilter} options={roles.map((role) => ({ value: role.role, text: role.role }))} />
        <MultiSelect label="Periods" selectedOptions={selectedPeriodIds} onChange={updatePeriodFilter} options={periods.map((period) => ({ value: period.id, text: formatPeriodName(period) }))} />
        <Button onClick={clearFilters} className="ml-2" size="sm" color="secondary">Clear</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {filteredRoles.map((role) => (
          <RoleStatistics key={role.role} role={role} splitByLevel={splitByLevel} periods={filteredPeriods} />
        ))}
      </div>
    </>
  )
}

const Statistics = () => {
	const client = useNextPeakClient()
  const { activeOrg } = getMe()
	const { data: roles, isLoading: isLoadingRoles } = useGetRoles(client)
	const { data: periods, isLoading: isLoadingPeriods } = useGetOrganizationPeriodsV2(client, { organizationId: activeOrg!.id })

  const isLoading = isLoadingRoles || isLoadingPeriods
  const dataAvailable = !!(!!roles?.length && periods)

	return (
		<Page title="Statistics">
			<div className="flex flex-col gap-10">
        {isLoading && <Loader />}
        {!isLoading && !roles?.length && <MutedText className="text-center">No roles available</MutedText>}
        {!isLoading && dataAvailable && <RolesGrid roles={roles} periods={periods} />}
			</div>
		</Page>
	)
}

export default Statistics
